import PropTypes from "prop-types"
import React, { useState } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormFeedback,
  Alert,
  Spinner,
  InputGroup,
  InputGroupText
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { Link } from "react-router-dom"
import withRouter from "components/Common/withRouter"
import logo from "assets/images/light-logo-coldblock.png"
import { setPasswordWithToken } from "helpers/backend-helper"
import PortalSupportNotice from "components/Common/PortalSupportNotice"

const SetPassword = (props) => {
  document.title = "Set Password - ColdBlock Technologies Inc.";

  const token = new URLSearchParams(window.location.search).get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const formDetails = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Password is required")
        .min(8, "At least 8 characters")
        .matches(/\d/, "Must include at least 1 number")
        .matches(/[A-Z]/, "Must include at least 1 uppercase letter")
        .matches(/[a-z]/, "Must include at least 1 lowercase letter")
        .matches(/[!@#$%^&*()[\]{}:;<>,.?/~_+\-=|\\]/, "Must include at least 1 special character")
        .matches(/^\S+$/, "No spaces allowed"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password")], "Passwords must match")
        .required("Confirm your password"),
    }),
    onSubmit: async (values) => {
      setError("");
      setSuccess("");
      setLoading(true);
      try {
        await setPasswordWithToken({ token, newPassword: values.password });
        setSuccess("Your password has been set. Redirecting to sign in…");
        setTimeout(() => {
          props.router.navigate("/signin");
        }, 1500);
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to set password. The link may be invalid or expired.");
      } finally {
        setLoading(false);
      }
    },
  });

  const Brand = (
    <div className="text-center">
      <Link to="/" className="logo-light-element d-inline-block">
        <img src={logo} alt="" height="65" />
      </Link>
    </div>
  );

  const Header = (
    <div className="bg-primary-subtle">
      <Row className="d-flex align-items-center justify-content-between">
        <Col className="col-6">
          <div className="text-primary p-4">
            <h5 className="text-primary white-txt">Set your password</h5>
          </div>
        </Col>
        <Col className="col-6">
          {Brand}
        </Col>
      </Row>
    </div>
  );

  return (
    <React.Fragment>
      <div className="account-pages vh-100 d-flex justify-content-center align-items-center">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                {Header}
                <CardBody className="pt-3">
                  <div className="p-2">
                    {!token ? (
                      <Alert color="danger" className="mb-3">This reset link is invalid.</Alert>
                    ) : (
                      <Form
                        className="form-horizontal"
                        onSubmit={(e) => { e.preventDefault(); formDetails.handleSubmit(); }}
                      >
                        {success && <Alert color="success" className="mb-3">{success}</Alert>}
                        {error && <Alert color="danger" className="mb-3">{error}</Alert>}

                        <div className="mb-3">
                          <Label className="form-label">Password</Label>
                          <InputGroup>
                            <Input
                              name="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Create a password"
                              value={formDetails.values.password}
                              onChange={formDetails.handleChange}
                              onBlur={formDetails.handleBlur}
                              autoComplete="new-password"
                              disabled={loading || !!success}
                              invalid={!!(formDetails.touched.password && formDetails.errors.password)}
                            />
                            <InputGroupText
                              role="button"
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              onClick={() => setShowPassword(v => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="bg-transparent"
                              style={{ cursor: "pointer" }}
                            >
                              <i className={showPassword ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                            </InputGroupText>
                          </InputGroup>
                          {formDetails.touched.password && formDetails.errors.password && (
                            <FormFeedback type="invalid" style={{ display: "block" }}>
                              {formDetails.errors.password}
                            </FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label className="form-label">Confirm password</Label>
                          <InputGroup>
                            <Input
                              name="confirmPassword"
                              type={showConfirm ? "text" : "password"}
                              placeholder="Re-enter password"
                              value={formDetails.values.confirmPassword}
                              onChange={formDetails.handleChange}
                              onBlur={formDetails.handleBlur}
                              autoComplete="new-password"
                              disabled={loading || !!success}
                              invalid={!!(formDetails.touched.confirmPassword && formDetails.errors.confirmPassword)}
                            />
                            <InputGroupText
                              role="button"
                              aria-label={showConfirm ? "Hide password" : "Show password"}
                              onClick={() => setShowConfirm(v => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              className="bg-transparent"
                              style={{ cursor: "pointer" }}
                            >
                              <i className={showConfirm ? "mdi mdi-eye-off" : "mdi mdi-eye"} />
                            </InputGroupText>
                          </InputGroup>
                          {formDetails.touched.confirmPassword && formDetails.errors.confirmPassword && (
                            <FormFeedback type="invalid" style={{ display: "block" }}>
                              {formDetails.errors.confirmPassword}
                            </FormFeedback>
                          )}
                        </div>

                        <div className="mt-3 d-grid">
                          <button
                            className="btn btn-primary primary-btn-bg"
                            type="submit"
                            disabled={loading || !!success || !formDetails.isValid}
                          >
                            {loading ? <Spinner size="sm" /> : "Set Password"}
                          </button>
                        </div>
                      </Form>
                    )}
                    <div className="mt-3 text-center">
                      <PortalSupportNotice />
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(SetPassword);

SetPassword.propTypes = {
  router: PropTypes.object,
}
